import {
  datadogAiAccessTokenPrefix,
  datadogAiAdminKeysSecretId,
  datadogAiConnectorName,
} from "@p0security/integrations-datadog-ai-shared/secret-manager";
import type { DatadogAiComponents } from "@p0security/integrations-datadog-ai-shared/types";
import type { FrontendInstallContext } from "@p0security/integrations-installer";
import {
  awsConnectorTf,
  gcpConnectorTf,
  p0InvokerEmail,
  p0InvokerRoleName,
  secretProviderFor,
  shellLines,
  tfId,
} from "@p0security/integrations-vaulted-credential-shared";
import type { ItemConfigOf } from "@p0security/shared/install/types";

type SecretManagerConfig = ItemConfigOf<
  DatadogAiComponents["credential"]
>["secretManager"];

export type InstallContext = FrontendInstallContext<unknown>;

const GCP_CONNECTOR_IMAGE = {
  imageName: "docker.io/p0security/p0-connector-datadog-ai-gcloud",
  digest:
    "sha-1528d3b@sha256:7bad3f07464b899e0e0a44e12b2b71afd0a31d434a7489cdf7558f607f941876",
} as const;

const AWS_CONNECTOR_IMAGE = {
  imageName: "p0security/p0-connector-datadog-ai-aws",
  digest:
    "sha-51ed50d@sha256:82cdc2e72fd3c7a8b59fbc6e673bc2961c5fa38d7035f57708f9fa89a7be627f",
} as const;

const awsAdminKeysSecretId = (organization: string) =>
  secretProviderFor("aws").replacer(datadogAiAdminKeysSecretId(organization));

export const awsStoreAdminKeys = (
  organization: string,
  secretManager: Extract<SecretManagerConfig, { type: "aws-sm" }>
) =>
  shellLines(
    `printf '{"apiKey":"%s","appKey":"%s"}' "$DD_API_KEY" "$DD_APP_KEY"`,
    "| aws secretsmanager put-secret-value",
    `--secret-id ${awsAdminKeysSecretId(organization)}`,
    `--region ${secretManager.secretsRegion}`,
    "--secret-string file:///dev/stdin"
  );

export const awsTf = (
  context: InstallContext,
  itemId: string,
  secretManager: Extract<SecretManagerConfig, { type: "aws-sm" }>
) =>
  awsConnectorTf({
    names: { resource: "datadog", label: "Datadog" },
    image: AWS_CONNECTOR_IMAGE,
    accountId: secretManager.install,
    region: secretManager.connectorRegion,
    secretsRegion: secretManager.secretsRegion,
    connectorName: datadogAiConnectorName,
    p0RoleName: p0InvokerRoleName(context),
    tokenSecretPrefix: secretProviderFor("aws").replacer(
      datadogAiAccessTokenPrefix(itemId)
    ),
    installSecret: {
      resource: "admin_keys",
      itemTfId: tfId(itemId),
      secretId: awsAdminKeysSecretId(itemId),
      label: "admin keys",
    },
  });

const gcpAdminKeysSecretId = (organization: string) =>
  secretProviderFor("gcp").replacer(datadogAiAdminKeysSecretId(organization));

export const gcpStoreAdminKeys = (
  organization: string,
  secretManager: Extract<SecretManagerConfig, { type: "gcp-sm" }>
) =>
  shellLines(
    `printf '{"apiKey":"%s","appKey":"%s"}' "$DD_API_KEY" "$DD_APP_KEY"`,
    `| gcloud secrets versions add ${gcpAdminKeysSecretId(organization)}`,
    `--project=${secretManager.install}`,
    "--data-file=-"
  );

export const gcpTf = (
  context: InstallContext,
  itemId: string,
  secretManager: Extract<SecretManagerConfig, { type: "gcp-sm" }>
) =>
  gcpConnectorTf({
    names: { resource: "datadog", role: "Datadog", label: "Datadog" },
    image: GCP_CONNECTOR_IMAGE,
    projectId: secretManager.install,
    region: secretManager.connectorRegion,
    connectorName: secretManager.connectorServiceName,
    serviceAccountId:
      secretManager.connectorServiceAccount?.split("@")[0] ?? "",
    invokerEmail: p0InvokerEmail(context.installed),
    tokenSecretPrefix: secretProviderFor("gcp").replacer(
      datadogAiAccessTokenPrefix(itemId)
    ),
    installSecret: {
      resource: "admin_keys",
      itemTfId: tfId(itemId),
      secretId: gcpAdminKeysSecretId(itemId),
      label: "admin keys",
    },
  });
